import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskReveal } from "./motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1524514587686-e2909d726e9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWVjaGFuaWNhbCUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3ODQ1NDIxMTF8MA&ixlib=rb-4.1.0&q=85";

const Hero = ({ onScrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.72, 0.92]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Precision mechanical detail" className="w-full h-full object-cover grayscale" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-[#0a0a0a]" />
      <div className="absolute inset-0 z-10 grid-lines opacity-[0.15]" aria-hidden="true" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/40" aria-hidden="true" />

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-20 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff4500] mb-8"
        >
          <span className="w-8 h-px bg-[#ff4500]" />
          Soft-Shell Assistive Exoskeletons
        </motion.div>

        <h1 className="font-display text-[13vw] sm:text-7xl lg:text-8xl xl:text-9xl font-semibold tracking-tighter leading-[0.92] text-[#f5f5f5]">
          <MaskReveal lines={["Lift more.", "Strain less."]} start={0.2} />
          <span className="block text-[#ff4500]">
            <MaskReveal lines={["Work safer."]} start={0.5} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 max-w-xl text-lg leading-relaxed text-[#a1a1aa]"
        >
          A wearable exoskeleton engineered with springs and hydraulics — soft where it
          touches, hard where it counts. Built to prevent musculoskeletal injury before it starts.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            data-testid="hero-cta-demo"
            onClick={() => onScrollTo("#contact")}
            className="group bg-[#ff4500] text-white px-8 py-4 font-mono-accent text-sm tracking-wide hover:bg-[#e03e00] active:scale-95 transition-all duration-200 flex items-center gap-3"
          >
            Request a Demo
            <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
          </button>
          <button
            data-testid="hero-cta-tech"
            onClick={() => onScrollTo("#technology")}
            className="border border-[#27272a] text-[#f5f5f5] px-8 py-4 font-mono-accent text-sm tracking-wide hover:border-[#ff4500] hover:text-[#ff4500] transition-colors duration-200"
          >
            Explore the Technology
          </button>
        </motion.div>
      </motion.div>

      {/* Stat strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="absolute bottom-0 left-0 right-0 z-20 border-t border-[#27272a] bg-[#0a0a0a]/60 backdrop-blur-sm"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#27272a]">
          {[
            ["Up to 40%", "reduced spinal load"],
            ["< 2.4 kg", "total wearable weight"],
            ["8 hrs+", "continuous support"],
            ["0 batteries", "passive spring-hydraulic core"],
          ].map(([n, l], i) => (
            <div key={i} className="py-6 px-4 first:pl-0">
              <div className="font-display text-2xl lg:text-3xl text-[#f5f5f5]">{n}</div>
              <div className="font-mono-accent text-[11px] uppercase tracking-wider text-[#71717a] mt-1">{l}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
